import { inject, Injectable } from "@angular/core";

import { capFetch, FetchError } from "@bitwarden/web-vault/utils/fetch";
import { CreditConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { IForexRateView } from "@bitwarden/web-vault/app/models/view/reference-data/forex-rate.view";
import {
  PluginConfigService,
  PluginIdentifier,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";

const CREDIT_CONFIG = process.env.CREDIT as unknown as CreditConfigType;

export interface GlossCreditsAuthResponse {
  user: { id: string; username: string; references: IForexRateView[] };
}

@Injectable({
  providedIn: "root",
})
export class GlossCreditsApi {
  readonly pluginName = PluginIdentifier.CREDIT;
  readonly baseUrl = CREDIT_CONFIG.url.replace(/\/$/, "");

  private pluginConfigService: PluginConfigService = inject(PluginConfigService);

  async auth(): Promise<GlossCreditsAuthResponse> {
    return this.query<GlossCreditsAuthResponse>(`${this.baseUrl}${CREDIT_CONFIG.endpoint.auth}`);
  }

  async registerUser(
    username: string,
    password: string,
    dynamoId?: string,
  ): Promise<{ message: string; user: { id: string } }> {
    const response = await capFetch<{ message: string; user: { id: string } }>({
      url: `${this.baseUrl}/api/auth/register`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: { username, password, ...(dynamoId ? { dynamoId } : {}) },
    });

    if (response.status < 200 || response.status > 299) {
      const errorData = response.data;
      throw new Error(errorData.message);
    }

    return response.data;
  }

  async generateApiKey(username: string, password: string): Promise<string> {
    const basicAuthKey = btoa(`${username}:${password}`);
    const response = await capFetch<{ message: string; apiKey: string }>({
      url: `${this.baseUrl}/api/auth/api-key/generate`,
      headers: { Authorization: `Basic ${basicAuthKey}` },
      method: "POST",
    });

    if (response.status < 200 || response.status > 299) {
      const errorData = response.data;
      throw new Error(errorData.message);
    }

    const { apiKey } = response.data;

    if (!apiKey) {
      throw new Error("API key not found in response");
    }

    return apiKey;
  }

  /** Link gloss-api Dynamo user id to the authenticated gloss-credit user. */
  async linkGlossUser(dynamoId: string): Promise<void> {
    const config = this.pluginConfigService.getConfig(this.pluginName);
    if (!config?.apiKey) {
      throw new Error("Credit API key is not available");
    }

    const response = await capFetch<{ message?: string; dynamoId?: string }>({
      url: `${this.baseUrl}${CREDIT_CONFIG.endpoint.gloss_link}`,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${config.apiKey}`,
      },
      data: { dynamoId },
    });

    if (response.status < 200 || response.status > 299) {
      const msg =
        response.data && typeof response.data === "object" && "message" in response.data
          ? String((response.data as { message?: string }).message)
          : `HTTP ${response.status}`;
      throw new Error(`Failed to link gloss user: ${msg}`);
    }
  }

  async query<T = never>(url: string): Promise<T> {
    const config = this.pluginConfigService.getConfig(this.pluginName);
    const headers: { "Content-Type": string; Authorization?: string } = {
      "Content-Type": "application/json",
    };

    if (config?.apiKey) {
      headers["Authorization"] = `Bearer ${config.apiKey}`;
    }

    const response = await capFetch<T>({
      url,
      method: "GET",
      headers,
    });

    if (response.status !== 200) {
      throw new FetchError("Network response was not ok", response.data);
    }

    return response.data;
  }
}
